// components/RegionalCompareView.jsx
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Normalize district name for matching
 */
function normalizeDistrictName(name) {
  return name
    .toLowerCase()
    .replace(/\s*(school district|sd|district|schools?)\s*/gi, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

/**
 * Regional Compare View
 * Expandable region sections showing district x pathway matrix
 * Shows ALL CIPs offered in a region (including zeros to highlight gaps)
 * Pattern inspired by NC credentials tier layout
 */
export default function RegionalCompareView({
  cipEnrolled,
  filterSession,
  nontrad,
  yearIndex = 4,
  regionalGroups = {},
  regionalColors = {}
}) {
  // Track which regions are expanded
  const [expandedRegions, setExpandedRegions] = useState(() =>
    new Set(Object.keys(regionalGroups))
  );

  // Track hover state for row highlighting
  const [hoverRow, setHoverRow] = useState(null); // { regionName, cipCode }

  // Process data into region-based structure
  // Shows ALL CIPs offered anywhere, so gaps are visible across regions
  const regionData = useMemo(() => {
    if (!cipEnrolled?.length) return { regions: [], allCips: [] };

    // Filter CIPs based on current filter session
    const filteredCips = cipEnrolled.filter((item) => {
      if (filterSession === "nontrad-female") {
        return nontrad?.female?.includes(item.cipcode);
      } else if (filterSession === "nontrad-male") {
        return nontrad?.male?.includes(item.cipcode);
      }
      return item.clusterName === filterSession || item.programArea === filterSession;
    });

    // First pass: collect ALL CIPs that have enrollment anywhere
    const allCipsMap = new Map(); // cipcode -> { code, name, totalEsd }
    const enrollmentMatrix = {}; // { cipcode: { districtName: enrollment } }

    filteredCips.forEach(cip => {
      if (!cip.enrolled) return;

      let cipTotal = 0;
      cip.enrolled.forEach(school => {
        if (school.schoolName === "Total") return;
        const enrollment = school.enrollment?.[yearIndex] ?? 0;
        if (enrollment > 0) {
          cipTotal += enrollment;
          if (!enrollmentMatrix[cip.cipcode]) {
            enrollmentMatrix[cip.cipcode] = {};
          }
          enrollmentMatrix[cip.cipcode][school.schoolName] = enrollment;
        }
      });

      if (cipTotal > 0) {
        allCipsMap.set(cip.cipcode, {
          code: cip.cipcode,
          name: cip.cipcodeName,
          totalEsd: cipTotal
        });
      }
    });

    // Sort all CIPs by total ESD enrollment
    const allCips = Array.from(allCipsMap.values())
      .sort((a, b) => b.totalEsd - a.totalEsd);

    // Build regions with ALL CIPs (even those with 0 in that region)
    const regions = Object.entries(regionalGroups).map(([name, districtList]) => {
      const districts = [...districtList].sort((a, b) => a.localeCompare(b));

      // For each CIP, calculate region total and district breakdown
      const cips = allCips.map(cipInfo => {
        const cipEnrollment = enrollmentMatrix[cipInfo.code] || {};
        let regionTotal = 0;
        const districtEnrollments = {};

        districts.forEach(district => {
          // Fuzzy match district names
          let enrollment = 0;
          Object.entries(cipEnrollment).forEach(([schoolName, enroll]) => {
            const normSchool = normalizeDistrictName(schoolName);
            const normDistrict = normalizeDistrictName(district);
            if (normSchool === normDistrict ||
                normSchool.includes(normDistrict) ||
                normDistrict.includes(normSchool)) {
              enrollment = enroll;
            }
          });
          districtEnrollments[district] = enrollment;
          regionTotal += enrollment;
        });

        return {
          code: cipInfo.code,
          name: cipInfo.name,
          total: regionTotal,
          totalEsd: cipInfo.totalEsd,
          districts: districtEnrollments
        };
      });

      const totalEnrollment = cips.reduce((sum, c) => sum + c.total, 0);
      const activeCipCount = cips.filter(c => c.total > 0).length;

      // Precompute district totals (GPT perf fix)
      const districtTotals = {};
      districts.forEach(d => {
        districtTotals[d] = cips.reduce((sum, cip) => sum + (cip.districts[d] || 0), 0);
      });

      // Precompute districts with/without participation
      const districtsWithParticipation = districts.filter(d => districtTotals[d] > 0);
      const districtsWithout = districts.filter(d => districtTotals[d] === 0);

      return {
        name,
        color: regionalColors[name] || '#6b7280',
        districts,
        cips,
        totalEnrollment,
        activeCipCount,
        totalCipCount: cips.length,
        districtTotals,
        districtsWithParticipation,
        districtsWithout
      };
    });

    return { regions, allCips };
  }, [cipEnrolled, filterSession, nontrad, yearIndex, regionalGroups, regionalColors]);

  // Toggle region expansion
  const toggleRegion = (regionName) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(regionName)) {
        next.delete(regionName);
      } else {
        next.add(regionName);
      }
      return next;
    });
  };

  // Get enrollment for a district in a CIP (direct lookup - fuzzy matching done in useMemo)
  const getDistrictEnrollment = (cip, district) => {
    return cip.districts[district] || 0;
  };

  if (!regionData.regions.length) {
    return (
      <div className="max-w-5xl">
        <div className="bg-white rounded-lg border border-zinc-200 p-12 text-center">
          <div className="text-zinc-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-zinc-700 mb-2">No enrollment data</h3>
          <p className="text-sm text-zinc-500">No pathways with enrollment found for this filter.</p>
        </div>
      </div>
    );
  }

  // Calculate grand totals
  const grandTotal = regionData.regions.reduce((sum, r) => sum + r.totalEnrollment, 0);
  const totalPathways = regionData.allCips.length;

  return (
    <div className="max-w-5xl">
      {/* Summary header */}
      <div className="mb-6 pb-4 border-b border-zinc-200">
        <div className="flex items-center gap-8 text-sm">
          <div>
            <span className="text-zinc-500">Total Enrollments:</span>
            <span className="ml-2 font-semibold text-zinc-800">{grandTotal.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-zinc-500">Pathways:</span>
            <span className="ml-2 font-semibold text-zinc-800">{totalPathways}</span>
          </div>
          <div>
            <span className="text-zinc-500">Regions:</span>
            <span className="ml-2 font-semibold text-zinc-800">{regionData.regions.length}</span>
          </div>
        </div>
      </div>

      {/* Region sections */}
      <div className="space-y-1">
        {regionData.regions.map(region => {
          const isExpanded = expandedRegions.has(region.name);

          return (
            <div key={region.name}>
              {/* Region header - clickable to expand/collapse */}
              <button
                type="button"
                onClick={() => toggleRegion(region.name)}
                aria-expanded={isExpanded}
                className="w-full flex items-center justify-between py-3 px-1 hover:bg-zinc-50 transition-colors border-b border-zinc-600"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-zinc-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-zinc-400" />
                  )}
                  <span className="font-semibold text-zinc-800">{region.name}</span>
                  <span className="text-xs text-zinc-400 font-normal">
                    {region.districts.length} districts
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-medium text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {region.totalEnrollment.toLocaleString()} enrollments
                  </span>
                  <span className="text-zinc-500">
                    across {region.activeCipCount} pathways
                  </span>
                </div>
              </button>

              {/* Expanded content - district x CIP matrix */}
              {isExpanded && (
                <div className="py-4 pl-8 pr-2" onMouseLeave={() => setHoverRow(null)}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      <thead>
                        <tr className="border-b border-zinc-200">
                          <th className="text-left py-2 pr-4 font-medium text-zinc-500 sticky left-0 bg-white" style={{ minWidth: 180 }}>
                            Pathway
                          </th>
                          {region.districts.map(district => (
                            <th
                              key={district}
                              className="text-center px-2 py-2 font-medium text-zinc-500"
                              style={{ minWidth: 60 }}
                              title={district}
                            >
                              <span className="block truncate" style={{ maxWidth: 70 }}>
                                {district.length > 10 ? district.slice(0, 8) + '…' : district}
                              </span>
                            </th>
                          ))}
                          <th className="text-right pl-4 py-2 font-medium text-zinc-500" style={{ minWidth: 60 }}>
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {region.cips.map((cip, idx) => {
                          const isRowHighlighted = hoverRow?.regionName === region.name && hoverRow?.cipCode === cip.code;
                          const hasRegionParticipation = cip.total > 0;

                          return (
                            <tr
                              key={cip.code}
                              className={`border-b border-zinc-100 transition-colors ${
                                isRowHighlighted ? 'bg-zinc-100' : idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'
                              }`}
                              onMouseEnter={() => setHoverRow({ regionName: region.name, cipCode: cip.code })}
                            >
                              <td className={`py-2 pr-4 sticky left-0 transition-colors ${
                                isRowHighlighted ? 'text-zinc-800 bg-zinc-100' :
                                hasRegionParticipation ? 'text-zinc-700 bg-inherit' : 'text-zinc-400 bg-inherit'
                              }`}>
                                <span className="block truncate" title={cip.name} style={{ maxWidth: 170 }}>
                                  {cip.name}
                                </span>
                              </td>
                              {region.districts.map(district => {
                                const enrollment = getDistrictEnrollment(cip, district);
                                const hasEnrollment = enrollment > 0;

                                return (
                                  <td key={district} className="text-center px-2 py-2">
                                    <span className={`inline-block min-w-[28px] ${hasEnrollment ? 'font-medium text-zinc-800' : 'text-zinc-300'}`}>
                                      {hasEnrollment ? enrollment : '—'}
                                    </span>
                                  </td>
                                );
                              })}
                              <td className={`text-right pl-4 py-2 font-semibold ${hasRegionParticipation ? 'text-zinc-700' : 'text-zinc-400'}`}>
                                {cip.total || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-zinc-200 bg-zinc-50">
                          <td className="py-2 pr-4 font-semibold text-zinc-700 sticky left-0 bg-zinc-50">
                            District Total
                          </td>
                          {region.districts.map(district => {
                            const total = region.districtTotals[district] || 0;
                            return (
                              <td key={district} className="text-center px-2 py-2 font-semibold text-zinc-600">
                                {total > 0 ? total : '—'}
                              </td>
                            );
                          })}
                          <td className="text-right pl-4 py-2 font-bold text-zinc-800">
                            {region.totalEnrollment}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
